import React, { useMemo } from 'react';
import {
  Text,
  StyleSheet,
  TextProps,
  TextStyle,
  StyleProp,
  Platform,
} from 'react-native';
import { accessibleTextProps } from '../../utils/accessibility-utils';
import { fitFontSize, fitLineHeight, FitFontSizeOptions } from './welcome-text-utils';

interface WelcomeFitTextProps {
  text: string;
  availableWidth: number;
  fitOptions: FitFontSizeOptions;
  lineHeightRatio: number;
  fontFamily: string;
  fontWeight: TextStyle['fontWeight'];
  color: string;
  style?: StyleProp<TextStyle>;
  accessibilityLabel?: string;
  textProps?: Pick<TextProps, 'numberOfLines' | 'adjustsFontSizeToFit' | 'minimumFontScale'>;
}

const WelcomeFitText: React.FC<WelcomeFitTextProps> = ({
  text,
  availableWidth,
  fitOptions,
  lineHeightRatio,
  fontFamily,
  fontWeight,
  color,
  style,
  accessibilityLabel,
  textProps,
}) => {
  const fontSize = useMemo(
    () => fitFontSize(text, availableWidth, fitOptions),
    [text, availableWidth, fitOptions]
  );
  const lineHeight = fitLineHeight(fontSize, lineHeightRatio);
  const useNativeFit =
    Platform.OS !== 'web' &&
    (textProps?.adjustsFontSizeToFit ?? true) &&
    (textProps?.numberOfLines ?? 1) === 1;

  return (
    <Text
      {...accessibleTextProps.body}
      accessibilityLabel={accessibilityLabel ?? text}
      style={[
        styles.base,
        {
          fontFamily,
          fontSize,
          fontWeight,
          lineHeight,
          color,
        },
        style,
      ]}
      numberOfLines={textProps?.numberOfLines ?? 1}
      adjustsFontSizeToFit={useNativeFit}
      minimumFontScale={
        textProps?.minimumFontScale ?? fitOptions.min / fitOptions.max
      }
    >
      {text}
    </Text>
  );
};

const styles = StyleSheet.create({
  base: {
    width: '100%',
    textAlign: 'center',
    overflow: 'visible',
  },
});

export default WelcomeFitText;
